import React from 'react';
import { Body1, Container, H5 } from '@ui';
import { TrackingEvent } from '@classes/TrackingEvent';
import { truncate } from '@utils/usefulFuncs';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { QuestionResult } from './components/QuestionResult';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { FormState } from '@ui/FormState';
import { ROUTES } from '@constants/routes';
import { AI_TOOLS, docTypes, getDefaultGptModelId } from '@constants';
import {
  addAdvancedSettingValues,
  useAdvancedSettings,
  useAdvancedSettingsSettled,
} from '@hooks/application/useAdvancedSettings';
import { isEarningsFilename, logFormSubmit, logNetRequest } from '@utils/usefulFuncs';
import {
  DOC_TEMPLATE_AGENT_KEY_METRICS_PROMPT,
  DOC_TEMPLATE_AGENT_Q_AND_A_PROMPT,
  DOC_TEMPLATE_AGENT_SUMMARY_PROMPT,
} from '@containers/gridMode';
import styled from 'styled-components';
import { isNewsArticle } from '@utils/doc';

const filingPrompt =
  'Write an analyst report detailing revenue and earning metrics, major updates, and outlook in bullets.';
const updatePrompt = 'Write an analyst report detailing major updates in a few bullets.';
const newsPrompt = 'Write an analyst report detailing major updates in a few bullets.';
const docTypeToPrompt = {
  '6-K': updatePrompt,
  '8-K': updatePrompt,
  DEF14A: updatePrompt,
  DEFA14A: updatePrompt,
  'S-1': filingPrompt,
  '10-K': filingPrompt,
  '10-Q': filingPrompt,
  '20-F': filingPrompt,
  '40-F': filingPrompt,
};
const defaultPrompt = DOC_TEMPLATE_AGENT_SUMMARY_PROMPT;

const alternateDocTypePrompts = {
  [docTypes.EARNINGS]: DOC_TEMPLATE_AGENT_Q_AND_A_PROMPT,
  [docTypes.USER_EARNINGS]: DOC_TEMPLATE_AGENT_Q_AND_A_PROMPT,
};

const ActionsWrapper = styled.div`
  padding-top: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
`;

const tool = AI_TOOLS.DOC_SUMMARY;

const keyMetricsPrompt = DOC_TEMPLATE_AGENT_KEY_METRICS_PROMPT;

export function SummarizeDocument({ doc }) {
  const {
    id,
    doc_type: _internal_docType,
    filename,

    security_symbol: ticker,

    is_transcript_available: hasTranscript,
    document_url,
  } = doc;

  const docType = isEarningsFilename(filename) ? docTypes.USER_EARNINGS : _internal_docType;
  const isEarningsDoc = docType === docTypes.EARNINGS || docType === docTypes.USER_EARNINGS;

  const advancedSettingsValues = useAdvancedSettings();
  // Settled = the doc_summary settings request has finished, whichever way. The page's
  // AdvancedSettingsButton issues that request; this component only waits on it.
  const isSettingsSettled = useAdvancedSettingsSettled(tool);
  // The settings endpoint 404s for a mode the user is not entitled to (or that has no row), and
  // the service resolves that as an empty list — so a configured `model` may never arrive. The
  // summary still has to run, so fall back to the catalog default rather than wait on it.
  const model = advancedSettingsValues.model || getDefaultGptModelId();
  const [resultId, setResultId] = React.useState(null);
  const [alternateResultId, setAlternateResultId] = React.useState(null);
  const [keyMetricsResultId, setKeyMetricsResultId] = React.useState(null);
  // A refused submit used to leave resultId null and nothing else, which the answer panel
  // renders as a spinner that never ends. The refusal is shown instead.
  const [submitError, setSubmitError] = React.useState(null);

  const hasOriginalDoc = document_url && document_url.length > 0 ? document_url : false;
  const selectedDocs = [id];
  const tickers = [ticker];

  const askQuestion = async ({ prompt: question }) => {
    TrackingEvent.create('View Recent Document Summary', {
      Question: question,
      Tickers: tickers.length === 0 ? null : tickers,
      Model: model,
      'Selected Docs': selectedDocs,
    });

    // `model` is set first so addAdvancedSettingValues leaves it alone: it is the configured
    // value when the mode supplies one, and the catalog default otherwise.
    const settings = { model, user_mode: tool };
    addAdvancedSettingValues(advancedSettingsValues, settings);
    logFormSubmit({ ...settings, question, doc_ids: selectedDocs, ticker: tickers });

    // Earnings summaries are scoped by doc_ids alone — the ticker adds nothing the document
    // doesn't already carry. Other doc types still send it, as a string: v2 takes exactly one
    // ticker and answers a one-element list with 400 "Multiple tickers not allowed".
    const result = await submitQueriesStream({
      questions: question,
      ticker: isEarningsDoc || !ticker ? null : ticker,
      docIds: selectedDocs,
      settings,
    });
    return result;
  };

  const rememberRefusal = (error) => setSubmitError(error || 'Something went wrong, try again.');

  const onViewOriginalDoc = () => {
    if (hasOriginalDoc) {
      window.open(document_url, '_blank');
    } else {
      window.open(ROUTES.docViewer + `?id=${id}`, '_blank');
    }
  };

  const onViewTranscriptDoc = () => {
    window.open(ROUTES.docViewer + `?id=${id}`, '_blank');
  };

  const onQueryDocument = () => {
    const url = ROUTES.digitalAnalyst + `?doc-ids=${id}`;
    window.open(url, '_blank');
  };

  React.useEffect(() => {
    const getPromptResponse = async () => {
      const prompt = isNewsArticle(docType) ? newsPrompt : docTypeToPrompt[docType] || defaultPrompt;
      const { status, runId, error } = await askQuestion({ prompt });

      if (status === 200) {
        setResultId(runId);
      } else {
        setResultId(null);
        rememberRefusal(error);
      }
    };
    const getAlternatePromptResponse = async () => {
      const prompt = alternateDocTypePrompts[docType];
      if (!prompt) return;

      const {
        status: keyMetricsStatus,
        runId: keyMetricsRunId,
        error: keyMetricsError,
      } = await askQuestion({ prompt: keyMetricsPrompt });

      if (keyMetricsStatus === 200) {
        setKeyMetricsResultId(keyMetricsRunId);
      } else {
        setKeyMetricsResultId(null);
        rememberRefusal(keyMetricsError);
      }

      const { status, runId, error } = await askQuestion({ prompt });

      if (status === 200) {
        setAlternateResultId(runId);
      } else {
        setAlternateResultId(null);
        rememberRefusal(error);
      }
    };

    // Wait for this mode's settings load to finish (loaded, 404'd, or failed) so the first submit
    // uses doc_summary's configured model rather than whatever the previous page left in the
    // store — and so it fires exactly once when the mode configures nothing at all.
    if (!isSettingsSettled) return;
    setSubmitError(null);
    getPromptResponse();
    getAlternatePromptResponse();
  }, [isSettingsSettled, model]);

  // A section whose submit was refused has no run to stream; showing its panel would show
  // the spinner the refusal message is there to replace.
  const showSection = (runId) => runId || !submitError;

  if (isEarningsDoc) {
    return (
      <Container>
        {docType === docTypes.USER_EARNINGS ? <H5>{`${filename}`}</H5> : <H5>{`${ticker} - ${docType}`}</H5>}
        <ActionsWrapper>
          {hasTranscript && (
            <Container>
              <TransparentFlatButton onClick={onViewTranscriptDoc}>View Original Transcript</TransparentFlatButton>
            </Container>
          )}
          {hasOriginalDoc ? (
            docType === docTypes.USER_EARNINGS ? (
              <Container>
                <TransparentFlatButton onClick={onViewOriginalDoc}>View Original Document</TransparentFlatButton>
              </Container>
            ) : (
              <Container>
                <TransparentFlatButton onClick={onViewOriginalDoc}>Listen Original Document</TransparentFlatButton>
              </Container>
            )
          ) : null}
          <Container>
            <TransparentFlatButton onClick={onQueryDocument}>Query Document</TransparentFlatButton>
          </Container>
        </ActionsWrapper>
        {submitError && (
          <Container top={24}>
            <FormState error={submitError} leftAligned />
          </Container>
        )}
        <Container top={24}>
          <Body1 bold>Summary</Body1>
          {showSection(resultId) && (
            <QuestionResult
              resultId={resultId}
              question={null}
              selectedDocs={selectedDocs}
              tickers={tickers}
              model={model}
              userMode={tool}
              enableMoreDetail
              collapseHeight
            />
          )}
        </Container>

        <Container top={24}>
          <Container bottom={10}>
            <Body1 bold>Key Metrics and Drivers</Body1>
          </Container>
          {showSection(keyMetricsResultId) && (
            <QuestionResult
              resultId={keyMetricsResultId}
              question={null}
              selectedDocs={selectedDocs}
              tickers={tickers}
              model={model}
              userMode={tool}
              enableMoreDetail
              collapseHeight
            />
          )}
        </Container>

        <Container top={24}>
          <Body1 bold>Analyst Q&A</Body1>
          {showSection(alternateResultId) && (
            <QuestionResult
              resultId={alternateResultId}
              question={null}
              selectedDocs={selectedDocs}
              tickers={tickers}
              model={model}
              userMode={tool}
              enableMoreDetail
              collapseHeight
            />
          )}
        </Container>
      </Container>
    );
  }

  const docText = [
    docTypes.MELIUS_COMPANY_REPORT,
    docTypes.MELIUS_INDUSTRY_REPORT,
    docTypes.MELIUS_WEEKLY_VIDEO_SLIDES,
    docTypes.MELIUS_INDUSTRIAL_STRATEGY,
  ].includes(docType)
    ? `${docType} - ${filename}`
    : `${truncate(ticker, 30)} - ${docType}`;
  return (
    <Container>
      <Container>
        <H5>{docText}</H5>
      </Container>
      {submitError && (
        <Container top={16}>
          <FormState error={submitError} leftAligned />
        </Container>
      )}
      <Container>
        {showSection(resultId) && (
          <QuestionResult
            resultId={resultId}
            question={null}
            selectedDocs={selectedDocs}
            tickers={tickers}
            model={model}
            userMode={tool}
            enableMoreDetail
            ActionsComponent={
              <ActionsWrapper>
                {hasTranscript && (
                  <Container>
                    <FlatButton onClick={onViewTranscriptDoc}>View Original Transcript</FlatButton>
                  </Container>
                )}
                {hasOriginalDoc && (
                  <Container>
                    <TransparentFlatButton onClick={onViewOriginalDoc}>View Original Document</TransparentFlatButton>
                  </Container>
                )}
                <Container>
                  <TransparentFlatButton onClick={onQueryDocument}>Query Document</TransparentFlatButton>
                </Container>
              </ActionsWrapper>
            }
          />
        )}
      </Container>
    </Container>
  );
}
